import { LeverageSlider } from '@ui/components/LeverageSlider';
import { NoSSR } from '@ui/components/NoSSR';
import { NORMAL_MIN_LEVERAGE } from '@ui/constants/uiConfig';
import { getPairMaxLeverage } from '@ui/features/MassiveToken/utils/getPairMaxLeverage';
import { Input } from '@ui/lib/park/ui/input';
import { RadioButtonGroup } from '@ui/lib/park/ui/radio-button-group';
import { Text } from '@ui/lib/park/ui/text';
import { OrderType } from '@ui/types/trade';
import { useTranslation } from 'next-i18next';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Box, BoxProps, Flex, HStack } from 'styled-system/jsx';

import { useHasUnlockDegenLeverage } from '../../hooks/useHasUnlockDegenLeverage';
import { useLeverageValue } from '../../store/useLeverage';
import {
  useGetBaseTradePair,
  useIsBasePairSupportDegen,
} from '../../store/useTradingPair';
import { useOrderType } from './store/useOrderType';
import { getLeverageArray } from './utils/getLeverageArray';

type LeverageSetterProps = BoxProps & {};

const DEGEN_LEVERAGE_QUICKS = [2, 10, 50, 100, 500, 750, 1001] as const;

const STANDARD_LEVERAGE_QUICKS_BY_MAX = {
  5: [1.5, 2, 3, 4, 5],
  25: [2, 5, 10, 15, 20, 25],
  100: [2, 3, 5, 10, 25, 50, 75, 100],
} as const satisfies Record<number, readonly number[]>;

function getStandardLeverageQuicks(maxLeverage: number): number[] {
  let base: readonly number[];

  if (maxLeverage <= 5) {
    base = STANDARD_LEVERAGE_QUICKS_BY_MAX[5];
  } else if (maxLeverage <= 25) {
    base = STANDARD_LEVERAGE_QUICKS_BY_MAX[25];
  } else {
    base = STANDARD_LEVERAGE_QUICKS_BY_MAX[100];
  }

  const filtered = base.filter((v) => v <= maxLeverage);

  // Ensure the last quick always matches the pair max (even if it's not exactly 5/25/100).
  if (filtered.length === 0 || filtered[filtered.length - 1] !== maxLeverage) {
    filtered.push(maxLeverage);
  }

  filtered.sort((a, b) => a - b);
  
return filtered;
}

export const LeverageSetter: React.FC<LeverageSetterProps> = ({ ...props }) => {
  const { t } = useTranslation();
  // const leverageValue = useLeverageValue((s => s.usePersistValue))();
  // const setLeverageValue = useLeverageValue((s => s.setPersistValue));
  const [leverage, setLeverage] = useLeverageValue((s) =>
    s.useDebouncedPersistState(200),
  );
  const deferredLeverage = useDeferredValue(leverage);
  const [inputValue, setInputValue] = useState<string>(String(leverage));
  const hasUnlockDegenLeverage = useHasUnlockDegenLeverage();
  const orderType = useOrderType((s) => s._persistValue);
  const isBasePairSupportDegen = useIsBasePairSupportDegen();
  const basePair = useGetBaseTradePair();
  const maxStandardLeverage = getPairMaxLeverage({ tradePair: basePair });
  const renderArray = getLeverageArray({
    isDegen: isBasePairSupportDegen && orderType !== OrderType.LIMIT, // do not support limit order & > 100x leverage
    baseTradePair: basePair,
  });

  const leverageQuicks = useMemo(() => {
    const shouldUseDegenQuicks =
      isBasePairSupportDegen && orderType !== OrderType.LIMIT;

    const quicks = shouldUseDegenQuicks
      ? [...DEGEN_LEVERAGE_QUICKS]
      : getStandardLeverageQuicks(maxStandardLeverage || 100);

    return quicks.map((v) => ({ value: v }));
  }, [isBasePairSupportDegen, maxStandardLeverage, orderType]);

  // useEffect(() => {
  //   setLeverage(leverageValue);
  // }, [leverageValue])

  useEffect(() => {
    const maxLeverage = leverageQuicks[leverageQuicks.length - 1].value;
    if (leverage > maxLeverage) {
      setLeverage(maxLeverage);
    }
  }, [leverage, leverageQuicks, setLeverage]);

  useEffect(() => {
    // keep input string in sync when leverage changes via slider/quick buttons
    setInputValue(String(leverage));
  }, [leverage]);

  return (
    <Box {...props}>
      <Flex justifyContent="space-between" mb={2}>
        <HStack gap={0}>
          <Text
            textStyle="v2.sectionLabel"
            color="v2.text.subtle"
          >
            {t('common.Leverage')}
          </Text>
          {/* <TextTooltip
            content={
              <VStack
                textStyle="text.small"
                maxW="220px"
                alignItems="flex-start"
                gap={4}
              >
                <Text>{t('common.This hourly rate is calculated per second based on position size and settled upon closing. Applies to open positions outside market hours.')}</Text>
                <VStack alignItems="flex-start" gap={1}>
                  <HStack gap={1}>
                    <Text color="price.priceUp">{t('common.Positive')}</Text>=
                    <Text>{t('common.Reward')}</Text>
                  </HStack>
                  <HStack gap={1}>
                    <Text color="price.priceDown">{t('common.Negative')}</Text>=
                    <Text>{t('common.Fee')}</Text>
                  </HStack>
                </VStack>
              </VStack>
            }
          >
            <Icon
              boxSize="10px"
              _hover={{
                cursor: 'pointer',
              }}
            >
              <Learn />
            </Icon>
          </TextTooltip> */}
        </HStack>
        <HStack
          gap={0}
          bg="v2.bg.surface1"
          border="1px solid"
          borderColor="v2.border.default"
          borderRadius="2px"
          _hover={{
            borderColor: 'v2.border.strong',
          }}
          _focusWithin={{
            borderColor: 'v2.border.focus',
          }}
          transition="border-color 0.15s"
          pr="6px"
        >
          <Input
            value={inputValue}
            onChange={(e) => {
              const raw = e.currentTarget.value;
              const digitsOnly = raw.replace(/\D/g, '');
              setInputValue(digitsOnly);
              if (digitsOnly !== '') {
                const next = parseFloat(digitsOnly);
                if (!Number.isNaN(next)) {
                  setLeverage(next);
                }
              }
            }}
            onBlur={() => {
              const maxQuickLeverage =
                leverageQuicks[leverageQuicks.length - 1]?.value ??
                NORMAL_MIN_LEVERAGE;
              let next = parseFloat(inputValue);
              if (Number.isNaN(next)) {
                next = leverage;
              }
              if (next < NORMAL_MIN_LEVERAGE) next = NORMAL_MIN_LEVERAGE;
              if (next > maxQuickLeverage) next = maxQuickLeverage;

              const maxStandardVal = renderArray[0]?.value;
              if (maxStandardVal && next > maxStandardVal) {
                for (let i = renderArray.length - 1; i >= 0; i--) {
                  if (next >= renderArray[i].value) {
                    next = renderArray[i].value;
                    break;
                  }
                }
              }

              setLeverage(next);
              setInputValue(String(next));
            }}
            inputMode="numeric"
            pattern="[0-9]*"
            aria-label="Leverage"
            bg="transparent"
            border="none"
            outline="none"
            p={0}
            h="auto"
            w="40px"
            textAlign="right"
            color="text.muted"
            textStyle="text.default"
            _focusVisible={{ outline: 'none', boxShadow: 'none' }}
            cursor="text"
            borderRadius="4px"
            padding="4px 4px"
            _hover={{
              bg: 'transparent',
            }}
            _focus={{
              bg: 'transparent',
            }}
          />
          <Text textStyle="text.default" color="text.muted">
            x
          </Text>
        </HStack>
      </Flex>
      <NoSSR>
        <LeverageSlider
          preventValue={
            !hasUnlockDegenLeverage ? maxStandardLeverage : undefined
          }
          value={[leverage]}
          min={NORMAL_MIN_LEVERAGE}
          step={0.1}
          marks={renderArray.map((v) => {
            return {
              ...v,
            };
          })}
          onValueChange={(v) => {
            setLeverage(v.value[0]);
          }}
        />
        {/* V2 preset chips: equal-width single-line grid, selected = long-dim tint */}
        <RadioButtonGroup.Root
          value={leverage.toString()}
          onValueChange={(v) => {
            setLeverage(parseFloat(v.value));
          }}
          display="grid"
          gridAutoFlow="column"
          gridAutoColumns="1fr"
          w="full"
          gap="6px"
          mt="2"
          size="sm"
        >
          {leverageQuicks.map((option, id) => (
            <RadioButtonGroup.Item
              minWidth="0"
              h="26px"
              justifyContent="center"
              borderRadius="2px"
              bg="v2.bg.surface2"
              border="1px solid"
              borderColor="v2.border.default"
              color="v2.text.muted"
              _checked={{
                color: 'v2.long.default',
                background: 'v2.long.dim',
                borderColor: 'v2.long.border',
              }}
              _hover={{
                background: 'v2.bg.hover',
              }}
              fontFamily="Inter"
              fontWeight={500}
              fontSize={option.value > 1000 ? '10px' : '11px'}
              style={{ fontVariantNumeric: 'tabular-nums' }}
              key={id}
              disabled={
                option.value > maxStandardLeverage! && !hasUnlockDegenLeverage
              }
              value={`${option.value}`}
            >
              <RadioButtonGroup.ItemControl />
              <RadioButtonGroup.ItemText>
                {option.value}x
              </RadioButtonGroup.ItemText>
              <RadioButtonGroup.ItemHiddenInput />
            </RadioButtonGroup.Item>
          ))}
        </RadioButtonGroup.Root>
      </NoSSR>
    </Box>
  );
};
